/**
 * SegmentedControl Component - Constants
 * =========================================
 * Tipos y mapas de estilo del control de segmentos (Figma: node 247:189,
 * "Segment controls"). Value-based leaf (mismo tipo de puente ngModel que
 * radio/chips — ver PASO 0 de `templates/leaf-component.md`): un solo
 * componente que renderiza sus opciones desde el binding `options` (array,
 * mismo criterio que `items` en `ecMenu`), no un compound con hijos.
 *
 * `bg` (Figma) alterna dos tratamientos completos (fondo, radios, colores),
 * no solo un color — ver BASE_CLASSES.containerBg/containerPlain y los pares
 * selected/unselected "Plain"/"OnBg".
 */

// ============================================================================
// TYPES
// ============================================================================

export interface SegmentedControlOption {
  value: string;
  label: string;
}

export interface SegmentedControlDefaultValues {
  bg: boolean;
}

// ============================================================================
// BASE
// ============================================================================

export const BASE_CLASSES = {
  container: 'ec-inline-flex ec-w-full ec-items-center ec-gap-2 ec-font-main',
  // bg=false: sin track, radio 12px por contenedor (Figma rounded-corners/l).
  containerPlain: 'ec-rounded-xl',
  // bg=true: track "sunken" (sin token exacto — mismo estand-in que ec-button
  // secondary/soft: ec-bg-gray-extralight) + padding. Radio full (pill): el
  // track y el segmento seleccionado son un estadio completo, no un rect con
  // esquinas redondeadas — ver referencia de diseño.
  containerBg: 'ec-bg-gray-extralight ec-p-1 ec-rounded-full',
  // Sin bg-transparent acá: es una utilidad background-color más, misma
  // especificidad que ec-bg-color-surface/ec-bg-color-primary-light — si
  // viviera en la base compartida, el orden en que Tailwind genera las
  // reglas puede hacer que le gane a la clase de la opción seleccionada y
  // tape el color (bug reportado: el pill seleccionado se veía transparente,
  // mostrando el track detrás). Por eso el bg-transparent va SOLO en `unselected`.
  option:
    'ec-flex-1 ec-min-w-0 ec-flex ec-items-center ec-justify-center ec-px-4 ec-py-3 ' +
    'ec-text-sm ec-font-normal ec-tracking-[-0.42px] ec-text-center ec-whitespace-nowrap ec-truncate ' +
    'ec-cursor-pointer ec-border-0 ec-transition-colors ' +
    'focus-visible:ec-outline-none focus-visible:ec-ring-2 focus-visible:ec-ring-color-primary focus-visible:ec-ring-offset-1',
  // Radio del segmento — depende de `bg` igual que el contenedor: 12px suelto
  // (Figma rounded-corners/l) vs. full/pill flotando sobre el track sunken.
  optionRadiusPlain: 'ec-rounded-xl',
  optionRadiusBg: 'ec-rounded-full',
  // No seleccionada: transparente en AMBOS modos (se apoya en lo que haya
  // detrás — la página si bg=false, el track sunken si bg=true).
  unselected: 'ec-bg-transparent ec-text-color-text-secondary',
  // bg=false — seleccionada: pill azul suave (mismo tratamiento que Button
  // intent=primary emphasis=soft: ec-bg-color-primary-light + texto -dark).
  selectedPlain: 'ec-bg-color-primary-light ec-text-color-primary-dark',
  // bg=true — seleccionada: pill blanco flotando sobre el track sunken.
  selectedOnBg: 'ec-bg-color-surface ec-text-color-text'
};

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULTS: SegmentedControlDefaultValues = {
  bg: false
};
